// Vitals reminder widget (CATCHUP_WIDGETS_PLAN.md Task 8, D-CU-2). Reminder-only
// -- no per-slot backfill for vitals in v1; nudges toward the existing vitals
// entry (VitalsWidget's own nav target, "charts") for each habitually-logged
// kind that's gone quiet. Self-fetching, null when absent/off.

import { useEffect, useState } from "react";
import type { CatchUpResponse } from "@medassist/shared";
import { resolveCatchUpWidgets, resolveProactiveSettings } from "@medassist/shared";
import { getCatchUpData } from "../../../lib/catchUpFetch.js";
import { ApiError } from "../../../lib/api.js";
import { HeartPulseIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

export function VitalsReminderWidget({ patient, onNavigate, onError }: WidgetProps) {
  const [response, setResponse] = useState<CatchUpResponse | null>(null);

  const enabled = resolveCatchUpWidgets(
    resolveProactiveSettings(patient.proactiveSuggestions).catchUpWidgets
  ).vitals;

  useEffect(() => {
    if (!enabled) return;
    getCatchUpData(patient.patientId)
      .then(setResponse)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load vitals reminder"));
  }, [patient.patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const section = response?.sections.find((s) => s.kind === "vitals");
  if (!section || section.items.length === 0) return null;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <HeartPulseIcon size={16} className="text-amber-500 dark:text-amber-400" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Vitals reminder</h2>
        <button onClick={() => onNavigate("charts")} className="ml-auto text-xs text-(--accent) hover:underline">
          Log vitals
        </button>
      </div>
      <ul className="space-y-0.5">
        {section.items.map((item) => (
          <li key={item.vitalKind} className="text-sm text-amber-700 dark:text-amber-300">
            {item.label} — last logged {item.gapDays} day{item.gapDays !== 1 ? "s" : ""} ago
          </li>
        ))}
      </ul>
    </section>
  );
}
